import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap"
import {
  upsertOverrideCanonico,
  deleteOverrideCanonico,
  getOverrideCanonico,
} from "./api"
import { TIPO_DOCUMENTO_DIAN, formatFecha, origenConfig } from "./helpers"

/**
 * Modal para crear / actualizar / eliminar el nombre canónico
 * (override manual) de un tercero. El back invalida cache automáticamente.
 *
 * Props:
 *  - abierto, onCerrar
 *  - tercero: objeto del response /analytics
 *  - nitemisor, actualizadoPor: contexto del usuario
 *  - onCambioGuardado: callback para que el container vuelva a cargar
 */
const EditarOverrideModal = ({ abierto, onCerrar, tercero, nitemisor, actualizadoPor, onCambioGuardado }) => {
  const [nombreCanonico, setNombreCanonico] = useState("")
  const [tipoDocumento, setTipoDocumento] = useState("31")
  const [notas, setNotas] = useState("")
  const [overrideExistente, setOverrideExistente] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [eliminando, setEliminando] = useState(false)
  const [error, setError] = useState("")
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Cargar override existente al abrir
  useEffect(() => {
    if (!abierto || !tercero) return
    setCargando(true)
    setError("")
    setConfirmDelete(false)
    setOverrideExistente(null)

    // Defaults desde el tercero
    setNombreCanonico(tercero.nombre_canonico || tercero.nombrereceptor || "")
    setTipoDocumento("31")
    setNotas("")

    getOverrideCanonico(tercero.nitreceptor, nitemisor)
      .then(res => {
        const ov = res.data?.data
        if (ov) {
          setOverrideExistente(ov)
          setNombreCanonico(ov.nombre_canonico || "")
          setTipoDocumento(ov.tipo_documento || "31")
          setNotas(ov.notas || "")
        }
      })
      .catch(err => {
        // 404 = no existe override → es OK, dejamos los defaults
        if (err.response?.status !== 404) {
          setError(err.response?.data?.error || err.message || "No se pudo consultar el override.")
        }
      })
      .finally(() => setCargando(false))
  }, [abierto, tercero, nitemisor])

  if (!tercero) return null

  const handleGuardar = async () => {
    setError("")
    if (!nombreCanonico.trim()) {
      setError("El nombre canónico no puede estar vacío.")
      return
    }
    setGuardando(true)
    try {
      await upsertOverrideCanonico({
        nitemisor,
        nitreceptor: tercero.nitreceptor,
        nombre_canonico: nombreCanonico.trim(),
        tipo_documento: tipoDocumento,
        actualizado_por: actualizadoPor,
        notas: notas.trim() || undefined,
      })
      onCambioGuardado?.()
      onCerrar()
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al guardar el override.")
    } finally {
      setGuardando(false)
    }
  }

  const handleEliminar = async () => {
    setError("")
    setEliminando(true)
    try {
      await deleteOverrideCanonico({
        nitemisor,
        nitreceptor: tercero.nitreceptor,
        actualizado_por: actualizadoPor,
      })
      onCambioGuardado?.()
      onCerrar()
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al eliminar el override.")
    } finally {
      setEliminando(false)
    }
  }

  const origen = origenConfig(tercero.nombre_canonico_origen)
  const variantes = tercero.nombre_variantes || []
  const cualquierAccion = guardando || eliminando

  return (
    <Modal isOpen={abierto} toggle={onCerrar} size="lg" backdrop={cualquierAccion ? "static" : true}>
      <ModalHeader toggle={cualquierAccion ? null : onCerrar}>
        <i className="mdi mdi-pencil-circle me-2 text-primary" />
        Editar nombre canónico
      </ModalHeader>
      <ModalBody>
        {/* Info del tercero */}
        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
            Tercero
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
            <span style={{
              fontSize: 14, fontFamily: "ui-monospace, SFMono-Regular, monospace",
              background: "#e0e7ff", color: "#3730a3", padding: "2px 10px", borderRadius: 6, fontWeight: 600,
            }}>
              {tercero.nitreceptor}
            </span>
            <span style={{ fontSize: 13, color: "#475569" }}>{tercero.nombrereceptor || "—"}</span>
          </div>
          {tercero.nombre_canonico && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
              Canónico actual: <strong style={{ color: "#0f172a" }}>{tercero.nombre_canonico}</strong>{" "}
              <span style={{
                background: origen.bg, color: origen.color,
                padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                textTransform: "uppercase", marginLeft: 4,
              }}>
                <i className={`mdi mdi-${origen.icon} me-1`} />
                {origen.label}
              </span>
            </div>
          )}
        </div>

        {/* Loading inicial */}
        {cargando && (
          <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>
            <Spinner size="sm" /> Buscando override existente…
          </div>
        )}

        {!cargando && (
          <>
            {/* Banner según existe o no */}
            {overrideExistente ? (
              <div style={{
                background: "#dbeafe", border: "1px solid #bfdbfe",
                color: "#1e40af", padding: "10px 14px", borderRadius: 8,
                marginBottom: 16, fontSize: 13,
              }}>
                <i className="mdi mdi-check-decagram me-1" />
                Existe un override <strong>manual</strong> registrado por <strong>{overrideExistente.actualizado_por || "—"}</strong>
                {" "}el {formatFecha(overrideExistente.actualizado_at)}. Estás editándolo.
              </div>
            ) : (
              <div style={{
                background: "#fef3c7", border: "1px solid #fde68a",
                color: "#92400e", padding: "10px 14px", borderRadius: 8,
                marginBottom: 16, fontSize: 13,
              }}>
                <i className="mdi mdi-information-outline me-1" />
                No hay override manual. El nombre actual viene del <strong>algoritmo automático</strong>.
                Al guardar se creará uno nuevo y se invalidará el cache del back.
              </div>
            )}

            {/* Variantes detectadas como sugerencias */}
            {variantes.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 6 }}>
                  Variantes detectadas (clic para usar)
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {variantes.map((v, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setNombreCanonico(v.nombre)}
                      style={{
                        background: nombreCanonico === v.nombre ? "#3b82f6" : "#f1f5f9",
                        color: nombreCanonico === v.nombre ? "#fff" : "#475569",
                        border: "none", borderRadius: 6,
                        padding: "4px 10px", fontSize: 12, cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                      title={`${v.facturas} facturas · último uso ${v.ultimo_uso}`}
                    >
                      {v.nombre} <span style={{ opacity: 0.6, marginLeft: 4 }}>({v.facturas})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Form */}
            <div className="mb-3">
              <label className="form-label small fw-semibold" style={{ color: "#334155" }}>
                Nombre canónico <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={nombreCanonico}
                onChange={e => setNombreCanonico(e.target.value)}
                disabled={cualquierAccion}
                placeholder="Ej. ALIMENTOS GARI S.A.S."
              />
              <div className="form-text" style={{ fontSize: 11 }}>
                Este es el nombre que se mostrará en reportes y exports.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold" style={{ color: "#334155" }}>
                Tipo de documento
              </label>
              <select
                className="form-select"
                value={tipoDocumento}
                onChange={e => setTipoDocumento(e.target.value)}
                disabled={cualquierAccion}
              >
                {TIPO_DOCUMENTO_DIAN.map(t => (
                  <option key={t.value} value={t.value}>{t.value} · {t.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold" style={{ color: "#334155" }}>
                Notas <span style={{ color: "#94a3b8", fontWeight: 400 }}>(opcional)</span>
              </label>
              <textarea
                className="form-control"
                rows={2}
                value={notas}
                onChange={e => setNotas(e.target.value)}
                disabled={cualquierAccion}
                placeholder="Ej. Nombre legal correcto según RUT"
              />
            </div>

            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                color: "#991b1b", padding: "10px 14px", borderRadius: 8,
                fontSize: 13,
              }}>
                <i className="mdi mdi-alert-circle me-1" />
                {error}
              </div>
            )}

            {/* Confirmación de eliminar */}
            {confirmDelete && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                color: "#991b1b", padding: "12px 16px", borderRadius: 8,
                marginTop: 12, fontSize: 13,
              }}>
                <strong>¿Eliminar el override manual?</strong>
                <div style={{ marginTop: 6 }}>
                  El tercero volverá a usar el algoritmo automático para determinar su nombre canónico.
                </div>
                <div className="d-flex gap-2 mt-2">
                  <button type="button" className="btn btn-sm btn-danger" onClick={handleEliminar} disabled={eliminando}>
                    {eliminando ? <Spinner size="sm" /> : "Sí, eliminar"}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmDelete(false)} disabled={eliminando}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {overrideExistente && !confirmDelete && !cargando && (
          <Button color="danger" outline onClick={() => setConfirmDelete(true)} disabled={cualquierAccion}>
            <i className="mdi mdi-trash-can-outline me-1" />
            Eliminar override
          </Button>
        )}
        <Button color="secondary" outline onClick={onCerrar} disabled={cualquierAccion}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleGuardar} disabled={cualquierAccion || cargando}>
          {guardando ? (
            <><Spinner size="sm" className="me-1" /> Guardando…</>
          ) : (
            <><i className="mdi mdi-content-save me-1" /> Guardar</>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

EditarOverrideModal.propTypes = {
  abierto: PropTypes.bool.isRequired,
  onCerrar: PropTypes.func.isRequired,
  tercero: PropTypes.object,
  nitemisor: PropTypes.string.isRequired,
  actualizadoPor: PropTypes.string,
  onCambioGuardado: PropTypes.func,
}

export default EditarOverrideModal
